import { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/crm/LoginPage";
import AuthCallback from "@/pages/crm/AuthCallback";
import ProtectedRoute from "@/components/crm/ProtectedRoute";
import SuperAdminOnly from "@/components/crm/SuperAdminOnly";
import useSEO from "@/lib/useSEO";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import seoPolicy from "@/content/seo-policy.json";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";

const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const CrmLayout = lazy(() => import("@/components/crm/CrmLayout"));
const Dashboard = lazy(() => import("@/pages/crm/Dashboard"));
const EmailSignature = lazy(() => import("@/pages/crm/EmailSignature"));
const ProjectsList = lazy(() => import("@/pages/crm/ProjectsList"));
const ProjectDetail = lazy(() => import("@/pages/crm/ProjectDetail"));
const SiteProjectsManager = lazy(() => import("@/pages/crm/SiteProjectsManager"));
const ProjectDetailPublic = lazy(() => import("@/pages/ProjectDetailPublic"));
const TeamMemberPage = lazy(() => import("@/pages/TeamMemberPage"));
const TeamListPage = lazy(() => import("@/pages/TeamListPage"));
const DelficoDistrictPage = lazy(() => import("@/pages/DelficoDistrictPage"));
const NewsPage = lazy(() => import("@/pages/NewsPage"));
const NewsManager = lazy(() => import("@/pages/crm/NewsManager"));
const Statistics = lazy(() => import("@/pages/crm/Statistics"));
const CommissionePaesaggio = lazy(() => import("@/pages/crm/paesaggio/CommissionePaesaggio"));
const PaesaggioDashboard = lazy(() => import("@/pages/crm/paesaggio/PaesaggioDashboard"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const LegacyTeamRedirect = lazy(() => import("@/pages/LegacyTeamRedirect"));
const PUBLIC_VIEWS = { home: HomePage, projects: ProjectsPage, project: ProjectDetailPublic,
  team: TeamListPage, person: TeamMemberPage, news: NewsPage, delfico: DelficoDistrictPage, legal: LegalPage };

function AppRouter() {
  const location = useLocation();
  useSEO();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // CRITICAL: Detect session_id or magic_token synchronously during render (not in useEffect)
  if (location.hash?.includes('session_id=') || location.hash?.includes('magic_token=')) {
    return <AuthCallback />;
  }

  return (
    <ChunkErrorBoundary routeKey={location.pathname}>
    <Routes>
      {seoPolicy.routes.map((route) => {
        const View = PUBLIC_VIEWS[route.view];
        return <Route key={route.path} path={route.path} element={<Suspense fallback={<div data-testid="public-route-loading" role="status" className="min-h-[50vh] flex items-center justify-center text-sm">Caricamento…</div>}><View {...route.props} /></Suspense>} />;
      })}
      {/* Legacy /firstnameLastname/ URLs from the old website → redirect to /team/{slug} */}
      <Route path="/:legacySlug" element={<Suspense fallback={null}><LegacyTeamRedirect /></Suspense>} />
      {/* Catch-all: any unknown public route → 404 page (noindex). */}
      <Route path="*" element={<Suspense fallback={null}><NotFoundPage /></Suspense>} />
      <Route path="/$$login" element={<LoginPage />} />
      <Route path="/crm" element={<ProtectedRoute><Suspense fallback={<div data-testid="crm-route-loading" role="status" className="p-8">Caricamento…</div>}><CrmLayout /></Suspense></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="projects" element={<ProjectsList />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="site-projects" element={<SiteProjectsManager />} />
        <Route path="email-signature" element={<EmailSignature />} />
        <Route path="news" element={<Suspense fallback={<div className="p-8 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" /></div>}><SuperAdminOnly><NewsManager /></SuperAdminOnly></Suspense>} />
        <Route path="stats" element={<Suspense fallback={<div className="p-8 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" /></div>}><SuperAdminOnly><Statistics /></SuperAdminOnly></Suspense>} />
        <Route path="paesaggio" element={<Suspense fallback={<div className="p-8 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" /></div>}><PaesaggioDashboard /></Suspense>} />
        <Route path="paesaggio/seduta/:sedutaSlug" element={<Suspense fallback={<div className="p-8 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" /></div>}><CommissionePaesaggio /></Suspense>} />
      </Route>
    </Routes>
    </ChunkErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
